import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Flex, Modal, Progress, Tag, Typography, Spin, Button } from 'antd';
import {
  ArrowRightOutlined,
  ClockCircleTwoTone,
  CheckCircleFilled,
} from '@ant-design/icons';
import { FetchMerchantOnboardingsV2Response } from '@shared/types';
import { t } from '../../../../utils/i18n';
import { changeLocation } from '../../../../actions';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAIAssistant } from '@shared/contexts/AIAssistantContext';
import EVENT_BUS_EVENTS from '../../../../constants/event-bus-events';
import { useLocalStorageState } from 'ahooks';
import { BETA_TOGGLES } from '../../../../features/beta/screens/Beta';
import { useAppSelector } from '@shared/hooks/use-app-selector';

export type OnboardingStep =
  FetchMerchantOnboardingsV2Response['steps'][number];

const STEP_ROUTES = {
  setup_address: '/settings',
  create_service: '/services',
  create_appointment: '/calendar',
  activate_subscription: '/settings/subscription',
  setup_shore_pay: '/application-settings/payments',
} as const;

const StyledOnboardingProgressModal = styled(Modal)`
  .ant-v5-modal-content {
    padding: 0;
    border-radius: 1.5rem;
    overflow: hidden;
  }

  .ant-v5-modal-footer {
    padding: 2rem 2.25rem;
  }

  .ant-v5-modal-header {
    margin-bottom: 0;
    padding: 1.5rem 2rem;
    background: transparent;
    border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorder};
  }
`;

const StyledList = styled(Flex)``;
const StyledListItem = styled(Flex)`
  cursor: pointer;
  padding: 1.25rem 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorder};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }
`;

const StyledListTag = styled(Tag)`
  font-size: 10px;
  padding: 0 0.5rem;
  display: flex;
  line-height: normal;
  align-items: center;
`;

const StyledListStatusSuccessIcon = styled(CheckCircleFilled)`
  font-size: 2.75rem;
`;
const StyledListStatusPendingIcon = styled(ClockCircleTwoTone)`
  font-size: 2.75rem;
`;

const StyledListStatusOptionalIcon = styled(ClockCircleTwoTone)`
  font-size: 2.75rem;
`;

const StyledListArrowIcon = styled(ArrowRightOutlined)`
  font-size: 1.5rem;
  color: rgba(0, 0, 0, 0.45) !important;
`;

const StyledAgentTag = styled(Tag)`
  font-size: 10px;
  padding: 0 1rem;
  display: flex;
  line-height: normal;
  align-items: center;
  border: none;
  border-radius: 15px;
  color: white;
  background-color: ${({ theme }) => theme.antd.colorPrimary};
`;

type OnboardingProgressModalProps = {
  onboardingStepsData: FetchMerchantOnboardingsV2Response | undefined;
  isLoadingOnboardingSteps: boolean;
  openModal: boolean;
  onCloseModalClick: () => void;
  onSkipStepClick: () => void;
};

export function OnboardingProgressModal({
  openModal = false,
  onCloseModalClick,
  onboardingStepsData,
  isLoadingOnboardingSteps,
  onSkipStepClick,
}: OnboardingProgressModalProps) {
  const dispatch = useDispatch();
  const { trackMethod, eventBus } = useGlobals();
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const { openAIAssistant, closeAIAssistant } = useAIAssistant();
  const onboardingSteps = onboardingStepsData?.steps;

  const totalTasks = onboardingSteps?.length ?? 0;
  const percentageCompleted =
    onboardingStepsData?.overall_progress_percentage ?? 0;
  const completedTasks =
    totalTasks > 0 ? Math.round((percentageCompleted / 100) * totalTasks) : 0;

  const shouldShowSkipStepButton =
    Array.isArray(onboardingSteps) &&
    onboardingSteps.length >= 4 &&
    onboardingSteps.slice(0, -1).every((step) => Boolean(step?.completed_at)) &&
    onboardingSteps[onboardingSteps.length - 1]?.is_mandatory === false;

  const [showOnboardingAIAssistant] = useLocalStorageState<boolean>(
    BETA_TOGGLES.smartOnboardingAIAssistant.key,
    {
      listenStorageChange: true,
    }
  );
  const isUserEligibleForAIAssistant =
    (showOnboardingAIAssistant ||
      (currentAccount?.locale === 'en' &&
        currentMerchant?.address?.country === 'DE')) &&
    !isLoadingOnboardingSteps;

  const handleOnCloseModalClick = () => {
    trackMethod('onboarding_progress_banner_dismissed', {
      platform: 'booking',
      overall_progress_percentage: percentageCompleted,
    });
    closeAIAssistant();
    onCloseModalClick();
  };

  const handleOnSkipStepClick = () => {
    trackMethod('onboarding_progress_banner_skip_step_clicked', {
      platform: 'booking',
      overall_progress_percentage: percentageCompleted,
    });
    onSkipStepClick();
  };

  const handleOnStepClick = (step: OnboardingStep) => {
    trackMethod('onboarding_progress_banner_step_clicked', {
      platform: 'booking',
      step_id: step.id,
      overall_progress_percentage: percentageCompleted,
    });

    if (step.has_agent && isUserEligibleForAIAssistant) {
      openAIAssistant({ stepId: step.id });
      return;
    }

    const route = STEP_ROUTES[step.id as keyof typeof STEP_ROUTES];

    if (route) {
      dispatch(changeLocation(route));
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, {
        payload: { route },
      });
      onCloseModalClick();
    }
  };

  return (
    <StyledOnboardingProgressModal
      centered
      open={openModal}
      footer={null}
      onCancel={handleOnCloseModalClick}
      data-testid="onboarding-progress-modal"
      title={
        !isLoadingOnboardingSteps ? (
          <Flex gap={10} align="center" justify="flex-start">
            <Flex vertical gap={0}>
              <Progress
                type="circle"
                percent={percentageCompleted}
                strokeColor="#00c2d1"
                size={35}
                format={() => null}
              />
            </Flex>
            <Flex vertical gap={0}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {t('onboarding-progress-modal.title')}
              </Typography.Title>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, fontWeight: 400, margin: 0 }}
              >
                {t('onboarding-progress-modal.description', {
                  completed_tasks: completedTasks,
                  total_tasks: totalTasks,
                })}
              </Typography.Text>
            </Flex>
          </Flex>
        ) : null
      }
    >
      {isLoadingOnboardingSteps && (
        <Flex align="center" justify="center" style={{ minHeight: 150 }}>
          <Spin />
        </Flex>
      )}
      {!isLoadingOnboardingSteps && onboardingStepsData && (
        <StyledList vertical gap={0}>
          {onboardingStepsData.steps.map((step) => (
            <StyledListItem
              key={step.id}
              onClick={() => handleOnStepClick(step)}
            >
              <Flex
                gap={10}
                align="center"
                justify="space-between"
                style={{ width: '100%' }}
              >
                <Flex gap={10} align="center">
                  <Flex align="center">
                    {step.completed_at && (
                      <StyledListStatusSuccessIcon
                        style={{ color: '#00d0be' }}
                      />
                    )}
                    {!step.completed_at && step.is_mandatory && (
                      <StyledListStatusPendingIcon twoToneColor="#a2a2a2" />
                    )}
                    {!step.completed_at && !step.is_mandatory && (
                      <StyledListStatusOptionalIcon twoToneColor="#8bbefc" />
                    )}
                  </Flex>
                  <Flex vertical gap={2}>
                    <Flex gap={10}>
                      <Typography.Text strong>{step.title}</Typography.Text>
                      {step.has_agent && isUserEligibleForAIAssistant && (
                        <StyledAgentTag>
                          ✦ {t('onboarding-progress-modal.tags.ai')}
                        </StyledAgentTag>
                      )}
                      {!step.is_mandatory && (
                        <StyledListTag color="blue" bordered>
                          {t('onboarding-progress-modal.tags.recommended')}
                        </StyledListTag>
                      )}
                    </Flex>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {step.description}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <StyledListArrowIcon />
              </Flex>
            </StyledListItem>
          ))}
        </StyledList>
      )}
      {!isLoadingOnboardingSteps && shouldShowSkipStepButton && (
        <Flex justify="end" style={{ margin: '0px 20px 20px' }}>
          <Button
            block
            size="small"
            type="primary"
            icon={<ArrowRightOutlined />}
            iconPosition="end"
            onClick={handleOnSkipStepClick}
          >
            {t('onboarding-progress-modal.buttons.skip-last-step')}
          </Button>
        </Flex>
      )}
    </StyledOnboardingProgressModal>
  );
}
